import { useState, useEffect, useRef, useMemo } from 'react';
import { filesApi, budgetsApi, settingsApi } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Upload,
  Download,
  FileSpreadsheet,
  FileText,
  Trash2,
  Eye,
  Clock,
  CheckCircle2,
  AlertCircle,
  FileUp,
  FileDown,
  Calculator,
  Building2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-ZA', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const IMPORT_TYPES = [
  { value: 'budget', label: 'Budget', description: 'Import budget figures from Excel template', icon: Calculator },
  { value: 'actuals', label: 'Actuals', description: 'Import actual figures for variance analysis', icon: FileSpreadsheet },
  { value: 'cashflow', label: 'Cashflow', description: 'Import cashflow projections', icon: FileText },
];

const EXPORT_DATA_TYPES = [
  { value: 'budget', label: 'Budget Only', description: 'Export budget figures only' },
  { value: 'actuals', label: 'Actuals Only', description: 'Export actual figures only' },
  { value: 'budget_vs_actuals', label: 'Budget vs Actuals', description: 'Export budget and actuals with variance' },
  { value: 'variance', label: 'Variance Report', description: 'Export variance analysis' },
  { value: 'forecast', label: 'Forecast Data', description: 'Export forecast figures' },
];

const ImportExportPage = () => {
  const [activeTab, setActiveTab] = useState('import');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [exportFormat, setExportFormat] = useState('xlsx');
  const [exportDataType, setExportDataType] = useState('budget');
  const [exportBusinessUnit, setExportBusinessUnit] = useState('all');
  const [exportFiscalYear, setExportFiscalYear] = useState('');
  const [exportingData, setExportingData] = useState(false);
  const [budgets, setBudgets] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  
  // Import dialog state
  const [importDialog, setImportDialog] = useState({ open: false, file: null });
  const [importType, setImportType] = useState('budget');
  const [importBuId, setImportBuId] = useState('');
  const [importFiscalYear, setImportFiscalYear] = useState('');
  const [importBudgetName, setImportBudgetName] = useState('');
  const [importProcessing, setImportProcessing] = useState(false);
  
  const fileInputRef = useRef(null);
  const { isDark } = useTheme();
  const { hasPermission, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [filesRes, busRes, budgetsRes] = await Promise.all([
        filesApi.getAll(),
        settingsApi.getBusinessUnits(),
        budgetsApi.getAll()
      ]);
      setFiles(filesRes.data);
      setBusinessUnits(busRes.data);
      setBudgets(budgetsRes.data);
      
      // Set default fiscal year to current
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth();
      // Fiscal year runs July-June, so if we're in Jan-Jun, we're in the previous fiscal year
      const fiscalYear = currentMonth >= 6 ? `${currentYear}/${currentYear + 1}` : `${currentYear - 1}/${currentYear}`;
      setExportFiscalYear(fiscalYear);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Get unique fiscal years from budgets
  const availableFiscalYears = useMemo(() => {
    const years = [...new Set(budgets.map(b => b.fiscal_year))].filter(Boolean);
    return years.sort((a, b) => b.localeCompare(a)); // Newest first
  }, [budgets]);

  // Sort business units alphabetically
  const sortedBusinessUnits = useMemo(() => {
    return [...businessUnits].sort((a, b) => a.name.localeCompare(b.name));
  }, [businessUnits]);

  // Filter budgets based on export selections
  const filteredBudgetsForExport = useMemo(() => {
    return budgets.filter(b => {
      if (exportBusinessUnit !== 'all' && b.business_unit_id !== exportBusinessUnit) return false;
      if (exportFiscalYear && b.fiscal_year !== exportFiscalYear) return false;
      return b.status === 'approved'; // Only export approved budgets
    });
  }, [budgets, exportBusinessUnit, exportFiscalYear]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      toast.error('Only Excel files (.xlsx, .xls) are supported');
      return;
    }

    // Open import dialog to ask what type of import
    setImportDialog({ open: true, file });
    setImportBudgetName(file.name.replace(/\.(xlsx|xls)$/i, ''));
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImportSubmit = async () => {
    if (!importDialog.file) return;
    
    if (importType === 'budget') {
      if (!importBuId) {
        toast.error('Please select a Business Unit');
        return;
      }
      if (!importFiscalYear) {
        toast.error('Please select a Fiscal Year');
        return;
      }
      if (!importBudgetName.trim()) {
        toast.error('Please enter a budget name');
        return;
      }
    }

    setImportProcessing(true);
    setUploadProgress(0);

    // Simulate progress
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => Math.min(prev + 10, 90));
    }, 200);

    try {
      // First upload the file
      const uploadResponse = await filesApi.upload(importDialog.file);
      
      // If importing as budget, create the budget
      if (importType === 'budget') {
        const bu = businessUnits.find(b => b.id === importBuId);
        await budgetsApi.importFromFile({
          file_id: uploadResponse.data.id,
          name: importBudgetName,
          business_unit_id: importBuId,
          business_unit_name: bu?.name || '',
          fiscal_year: importFiscalYear
        });
        toast.success(`Budget "${importBudgetName}" imported successfully! View it in the Budgets page.`);
        navigate('/budgets');
      } else {
        toast.success(`File uploaded. ${importType} import coming soon.`);
      }

      setUploadProgress(100);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import file');
    } finally {
      clearInterval(progressInterval);
      setImportProcessing(false);
      setUploadProgress(0);
      setImportDialog({ open: false, file: null });
      setImportType('budget');
      setImportBuId('');
      setImportFiscalYear('');
      setImportBudgetName('');
    }
  };

  const handleDeleteFile = async (id) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;
    
    try {
      await filesApi.delete(id);
      setFiles(prev => prev.filter(f => f.id !== id));
      toast.success('File deleted');
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const handleExport = async () => {
    if (!exportFiscalYear) {
      toast.error('Please select a Fiscal Year');
      return;
    }
    
    if (filteredBudgetsForExport.length === 0) {
      toast.error('No approved budgets found for the selected filters');
      return;
    }

    setExportingData(true);
    
    try {
      // Build filename: BUCode_FiscalYear_DataType.format
      const buName = exportBusinessUnit === 'all' 
        ? 'Consolidated' 
        : businessUnits.find(b => b.id === exportBusinessUnit)?.code || 'BU';
      const fyFormatted = exportFiscalYear.replace('/', '-');
      const dataTypeLabel = EXPORT_DATA_TYPES.find(t => t.value === exportDataType)?.label.replace(/\s+/g, '') || 'Data';
      const filename = `${buName}_${fyFormatted}_${dataTypeLabel}.${exportFormat}`;

      // For now, generate CSV export for budget data
      if (exportFormat === 'csv' || exportFormat === 'xlsx') {
        const MONTHS = ['jul', 'aug', 'sep', 'oct', 'nov', 'dec', 'jan', 'feb', 'mar', 'apr', 'may', 'jun'];
        const MONTH_NAMES = { jul: 'July', aug: 'August', sep: 'September', oct: 'October', nov: 'November', dec: 'December', jan: 'January', feb: 'February', mar: 'March', apr: 'April', may: 'May', jun: 'June' };
        
        let csvContent = '';
        
        // Build header based on data type
        if (exportDataType === 'budget') {
          csvContent = `Account Code,Account Name,Category,${MONTHS.map(m => MONTH_NAMES[m]).join(',')},Total\n`;
        } else if (exportDataType === 'actuals') {
          csvContent = `Account Code,Account Name,Category,${MONTHS.map(m => `${MONTH_NAMES[m]} Actual`).join(',')},Total Actual\n`;
        } else if (exportDataType === 'budget_vs_actuals' || exportDataType === 'variance') {
          csvContent = `Account Code,Account Name,Category,${MONTHS.map(m => `${MONTH_NAMES[m]} Budget,${MONTH_NAMES[m]} Actual,${MONTH_NAMES[m]} Variance`).join(',')},Total Budget,Total Actual,Total Variance\n`;
        }
        
        // Aggregate data from all matching budgets
        const aggregatedItems = {};
        
        for (const budget of filteredBudgetsForExport) {
          for (const item of (budget.line_items || [])) {
            if (item.is_header) continue;
            
            const key = item.account_code;
            if (!aggregatedItems[key]) {
              aggregatedItems[key] = {
                account_code: item.account_code,
                account_name: item.account_name,
                category: item.category || '',
                ...MONTHS.reduce((acc, m) => ({ ...acc, [m]: 0, [`${m}_actual`]: 0 }), {})
              };
            }
            
            // Aggregate values
            for (const month of MONTHS) {
              aggregatedItems[key][month] += (item[month] || 0);
              aggregatedItems[key][`${month}_actual`] += (item[`${month}_actual`] || 0);
            }
          }
        }
        
        // Generate rows
        for (const item of Object.values(aggregatedItems)) {
          const budgetTotal = MONTHS.reduce((sum, m) => sum + (item[m] || 0), 0);
          const actualTotal = MONTHS.reduce((sum, m) => sum + (item[`${m}_actual`] || 0), 0);
          
          if (exportDataType === 'budget') {
            const values = MONTHS.map(m => item[m] || 0);
            csvContent += `${item.account_code},"${item.account_name}","${item.category}",${values.join(',')},${budgetTotal}\n`;
          } else if (exportDataType === 'actuals') {
            const values = MONTHS.map(m => item[`${m}_actual`] || 0);
            csvContent += `${item.account_code},"${item.account_name}","${item.category}",${values.join(',')},${actualTotal}\n`;
          } else if (exportDataType === 'budget_vs_actuals' || exportDataType === 'variance') {
            const values = MONTHS.map(m => {
              const budget = item[m] || 0;
              const actual = item[`${m}_actual`] || 0;
              return `${budget},${actual},${actual - budget}`;
            });
            csvContent += `${item.account_code},"${item.account_name}","${item.category}",${values.join(',')},${budgetTotal},${actualTotal},${actualTotal - budgetTotal}\n`;
          }
        }
        
        // Download file
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        toast.success(`Exported ${Object.keys(aggregatedItems).length} accounts to ${filename}`);
      } else {
        toast.info(`PDF export coming soon. For now, please use Excel or CSV format.`);
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    } finally {
      setExportingData(false);
    }
  };

  // Generate fiscal year options
  const currentYear = new Date().getFullYear();
  const fiscalYears = [];
  for (let i = currentYear - 2; i <= currentYear + 2; i++) {
    fiscalYears.push(`${i}/${i + 1}`);
  }

  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="import-export-loading">
        <Skeleton className="h-10 w-64 rounded-sm" />
        <Skeleton className="h-96 rounded-sm" />
      </div>
    );
  }

  return (
    <div className={`p-6 space-y-6 ${isDark ? 'text-white' : ''}`} data-testid="import-export-page">
      {/* Header */}
      <div>
        <h1 className={`font-display text-3xl font-light ${isDark ? 'text-white' : 'text-slate-900'}`}>
          Import & Export
        </h1>
        <p className={isDark ? 'text-zinc-400 mt-1' : 'text-slate-500 mt-1'}>
          Import budget data from Excel files and export reports
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={`p-1 rounded-sm ${isDark ? 'bg-zinc-800' : 'bg-slate-100'}`}>
          <TabsTrigger 
            value="import" 
            className={`rounded-sm ${isDark ? 'data-[state=active]:bg-zinc-700' : 'data-[state=active]:bg-white'}`}
            data-testid="tab-import"
          >
            <FileUp className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Import Data
          </TabsTrigger>
          <TabsTrigger 
            value="export" 
            className={`rounded-sm ${isDark ? 'data-[state=active]:bg-zinc-700' : 'data-[state=active]:bg-white'}`}
            data-testid="tab-export"
          >
            <FileDown className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Export Data
          </TabsTrigger>
        </TabsList>

        {/* Import Tab */}
        <TabsContent value="import" className="mt-6 space-y-6">
          {/* Upload Area */}
          {hasPermission('write') && (
            <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
              <CardHeader className={`border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                <CardTitle className="font-display text-lg font-medium flex items-center gap-2">
                  <Upload className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
                  Upload Excel Files
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".xlsx,.xls"
                  className="hidden"
                  data-testid="file-input"
                />
                
                <div 
                  className={`border-2 border-dashed rounded-sm p-8 text-center cursor-pointer transition-colors ${
                    isDark 
                      ? 'border-zinc-700 hover:border-zinc-600 hover:bg-zinc-800/50' 
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="upload-dropzone"
                >
                  <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center ${
                    isDark ? 'bg-zinc-800' : 'bg-slate-100'
                  }`}>
                    <Upload className="w-6 h-6 text-emerald-600" strokeWidth={1.5} />
                  </div>
                  <p className={`mt-4 font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Click to upload or drag and drop
                  </p>
                  <p className={`mt-1 text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                    Excel files only (.xlsx, .xls)
                  </p>
                </div>

                <div className={`mt-4 p-4 rounded-sm ${isDark ? 'bg-zinc-800' : 'bg-slate-50'}`}>
                  <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Supported import types:
                  </p>
                  <ul className={`mt-2 text-sm space-y-1 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                    <li>• <strong>Budget</strong> - Import budget figures (creates new budget in Budgets page)</li>
                    <li>• <strong>Actuals</strong> - Import actual figures for variance analysis</li>
                    <li>• <strong>Cashflow</strong> - Import cashflow projections</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Uploaded Files List */}
          <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
            <CardHeader className={`border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
              <CardTitle className="font-display text-lg font-medium">
                Uploaded Files ({files.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {files.length > 0 ? (
                <div className={`divide-y ${isDark ? 'divide-zinc-800' : 'divide-slate-100'}`}>
                  {files.map((file) => (
                    <div 
                      key={file.id} 
                      className={`p-4 flex items-center justify-between ${
                        isDark ? 'hover:bg-zinc-800' : 'hover:bg-slate-50'
                      }`}
                      data-testid={`file-${file.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${
                          isDark ? 'bg-emerald-900/30' : 'bg-emerald-50'
                        }`}>
                          <FileSpreadsheet className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
                        </div>
                        <div>
                          <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            {file.filename}
                          </p>
                          <div className={`flex items-center gap-3 text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDate(file.uploaded_at)}
                            </span>
                            <span>{formatFileSize(file.size)}</span>
                            <span className={`px-2 py-0.5 rounded-sm text-xs ${
                              file.status === 'processed' 
                                ? isDark ? 'bg-emerald-900/30 text-emerald-400' : 'bg-emerald-50 text-emerald-700'
                                : file.status === 'imported_as_budget'
                                ? isDark ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-50 text-blue-700'
                                : isDark ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-50 text-amber-700'
                            }`}>
                              {file.status === 'processed' ? (
                                <span className="flex items-center gap-1">
                                  <CheckCircle2 className="w-3 h-3" />
                                  Processed
                                </span>
                              ) : file.status === 'imported_as_budget' ? (
                                <span className="flex items-center gap-1">
                                  <Calculator className="w-3 h-3" />
                                  Imported as Budget
                                </span>
                              ) : (
                                <span className="flex items-center gap-1">
                                  <AlertCircle className="w-3 h-3" />
                                  {file.status}
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => navigate(`/files/${file.id}`)}
                          className="rounded-sm"
                          data-testid={`view-file-${file.id}`}
                        >
                          <Eye className="w-4 h-4" strokeWidth={1.5} />
                        </Button>
                        {hasPermission('delete') && (
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleDeleteFile(file.id)}
                            className="rounded-sm text-red-600"
                            data-testid={`delete-file-${file.id}`}
                          >
                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={`p-8 text-center ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                  <FileSpreadsheet className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-zinc-600' : 'text-slate-300'}`} strokeWidth={1.5} />
                  <p>No files uploaded yet</p>
                  <p className="text-sm mt-1">Upload your first Excel file to get started</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Export Tab */}
        <TabsContent value="export" className="mt-6">
          <Card className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
            <CardHeader className={`border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
              <CardTitle className="font-display text-lg font-medium flex items-center gap-2">
                <Download className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
                Export Data
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* Filter Row */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Business Unit</Label>
                  <Select value={exportBusinessUnit} onValueChange={setExportBusinessUnit}>
                    <SelectTrigger className="rounded-sm" data-testid="export-business-unit">
                      <Building2 className="w-4 h-4 mr-2 text-slate-400" />
                      <SelectValue placeholder="Select BU" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Business Units (Consolidated)</SelectItem>
                      {sortedBusinessUnits.map(bu => (
                        <SelectItem key={bu.id} value={bu.id}>{bu.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Fiscal Year *</Label>
                  <Select value={exportFiscalYear} onValueChange={setExportFiscalYear}>
                    <SelectTrigger className="rounded-sm" data-testid="export-fiscal-year">
                      <SelectValue placeholder="Select FY" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableFiscalYears.map(fy => (
                        <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>
                      ))}
                      {availableFiscalYears.length === 0 && (
                        <SelectItem value="" disabled>No budgets available</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Data Type</Label>
                  <Select value={exportDataType} onValueChange={setExportDataType}>
                    <SelectTrigger className="rounded-sm" data-testid="export-data-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPORT_DATA_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Export Format</Label>
                  <Select value={exportFormat} onValueChange={setExportFormat}>
                    <SelectTrigger className="rounded-sm" data-testid="export-format">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="xlsx">
                        <div className="flex items-center gap-2">
                          <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                          Excel (.xlsx)
                        </div>
                      </SelectItem>
                      <SelectItem value="csv">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-blue-600" />
                          CSV (.csv)
                        </div>
                      </SelectItem>
                      <SelectItem value="pdf">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-red-600" />
                          PDF Document (.pdf)
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Export Preview */}
              <div className={`p-4 rounded-sm ${isDark ? 'bg-zinc-800' : 'bg-slate-50'}`}>
                <p className={`text-sm font-medium mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Export Preview
                </p>
                <div className={`text-sm space-y-1 ${isDark ? 'text-zinc-300' : 'text-slate-600'}`}>
                  <p>
                    <strong>Business Unit:</strong> {exportBusinessUnit === 'all' ? 'All (Consolidated)' : businessUnits.find(b => b.id === exportBusinessUnit)?.name || '-'}
                  </p>
                  <p>
                    <strong>Fiscal Year:</strong> {exportFiscalYear ? `FY ${exportFiscalYear}` : 'Not selected'}
                  </p>
                  <p>
                    <strong>Data Type:</strong> {EXPORT_DATA_TYPES.find(t => t.value === exportDataType)?.label || '-'}
                  </p>
                  <p>
                    <strong>Format:</strong> {exportFormat.toUpperCase()}
                  </p>
                  <p className={`mt-2 pt-2 border-t ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>
                    <strong>Budgets to export:</strong> {filteredBudgetsForExport.length} approved budget(s)
                    {filteredBudgetsForExport.length > 0 && (
                      <span className={`block text-xs mt-1 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                        {filteredBudgetsForExport.map(b => b.business_unit_name || b.name).join(', ')}
                      </span>
                    )}
                  </p>
                </div>
              </div>

              {/* Warning if no data */}
              {filteredBudgetsForExport.length === 0 && exportFiscalYear && (
                <div className={`p-3 rounded-sm flex items-center gap-3 ${isDark ? 'bg-amber-900/20 border border-amber-800' : 'bg-amber-50 border border-amber-200'}`}>
                  <AlertCircle className={`w-5 h-5 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                  <p className={`text-sm ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                    No approved budgets found for the selected filters. Only approved budgets can be exported.
                  </p>
                </div>
              )}

              {/* Export Button */}
              <div className="flex justify-end">
                <Button 
                  onClick={handleExport}
                  disabled={!exportFiscalYear || filteredBudgetsForExport.length === 0 || exportingData}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
                  data-testid="export-btn"
                >
                  {exportingData ? (
                    <>
                      <Clock className="w-4 h-4 mr-2 animate-spin" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      Export {exportFormat.toUpperCase()}
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Export Templates */}
          <Card className={`border shadow-none rounded-sm mt-6 ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}>
            <CardHeader className={`border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
              <CardTitle className="font-display text-lg font-medium">
                Download Templates
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { name: 'Budget Template', description: 'Standard budget import template', icon: FileSpreadsheet },
                  { name: 'Actuals Template', description: 'Monthly actuals import format', icon: FileSpreadsheet },
                  { name: 'Cashflow Template', description: 'Cashflow projection template', icon: FileSpreadsheet },
                ].map((template, i) => (
                  <div 
                    key={i}
                    className={`p-4 border rounded-sm cursor-pointer transition-colors ${
                      isDark 
                        ? 'border-zinc-700 hover:border-zinc-600 hover:bg-zinc-800' 
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                    data-testid={`template-${i}`}
                  >
                    <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${
                      isDark ? 'bg-emerald-900/30' : 'bg-emerald-50'
                    }`}>
                      <template.icon className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
                    </div>
                    <p className={`mt-3 font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {template.name}
                    </p>
                    <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                      {template.description}
                    </p>
                    <Button 
                      variant="link" 
                      className="p-0 h-auto mt-2 text-emerald-600"
                      onClick={() => toast.info('Template download started')}
                    >
                      <Download className="w-3 h-3 mr-1" />
                      Download
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Import Type Dialog */}
      <Dialog open={importDialog.open} onOpenChange={(open) => !open && !importProcessing && setImportDialog({ open: false, file: null })}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileUp className="w-5 h-5 text-emerald-600" />
              Import File
            </DialogTitle>
            <DialogDescription>
              Select the type of data you're importing and configure options.
            </DialogDescription>
          </DialogHeader>

          {importProcessing ? (
            <div className="py-8 text-center">
              <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${
                isDark ? 'bg-zinc-800' : 'bg-slate-100'
              }`}>
                <FileSpreadsheet className="w-8 h-8 text-emerald-600 animate-pulse" strokeWidth={1.5} />
              </div>
              <p className={`mt-4 font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Processing import...
              </p>
              <Progress value={uploadProgress} className="mt-4 max-w-xs mx-auto" />
              <p className={`text-sm mt-2 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                {uploadProgress < 50 ? 'Uploading file...' : 'Creating budget...'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* File Info */}
              <div className={`p-3 rounded-sm flex items-center gap-3 ${isDark ? 'bg-zinc-800' : 'bg-slate-50'}`}>
                <FileSpreadsheet className="w-8 h-8 text-emerald-600" strokeWidth={1.5} />
                <div>
                  <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {importDialog.file?.name}
                  </p>
                  <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-500'}`}>
                    {importDialog.file ? formatFileSize(importDialog.file.size) : ''}
                  </p>
                </div>
              </div>

              {/* Import Type Selection */}
              <div className="space-y-2">
                <Label>Import Type</Label>
                <div className="grid grid-cols-3 gap-2">
                  {IMPORT_TYPES.map(type => (
                    <div
                      key={type.value}
                      className={`p-3 border rounded-sm cursor-pointer transition-all ${
                        importType === type.value
                          ? 'border-emerald-600 bg-emerald-50 dark:bg-emerald-900/20'
                          : isDark ? 'border-zinc-700 hover:border-zinc-600' : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => setImportType(type.value)}
                    >
                      <type.icon className={`w-5 h-5 ${importType === type.value ? 'text-emerald-600' : isDark ? 'text-zinc-400' : 'text-slate-400'}`} />
                      <p className={`text-sm font-medium mt-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {type.label}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Budget-specific options */}
              {importType === 'budget' && (
                <>
                  <div className="space-y-2">
                    <Label>Budget Name *</Label>
                    <Input
                      value={importBudgetName}
                      onChange={(e) => setImportBudgetName(e.target.value)}
                      placeholder="e.g., FY 2024/2025 Annual Budget"
                      className="rounded-sm"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Business Unit *</Label>
                      <Select value={importBuId} onValueChange={setImportBuId}>
                        <SelectTrigger className="rounded-sm">
                          <Building2 className="w-4 h-4 mr-2 text-slate-400" />
                          <SelectValue placeholder="Select BU" />
                        </SelectTrigger>
                        <SelectContent>
                          {businessUnits.map(bu => (
                            <SelectItem key={bu.id} value={bu.id}>{bu.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Fiscal Year *</Label>
                      <Select value={importFiscalYear} onValueChange={setImportFiscalYear}>
                        <SelectTrigger className="rounded-sm">
                          <SelectValue placeholder="Select FY" />
                        </SelectTrigger>
                        <SelectContent>
                          {fiscalYears.map(fy => (
                            <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className={`p-3 rounded-sm text-sm ${isDark ? 'bg-emerald-900/20 text-emerald-400' : 'bg-emerald-50 text-emerald-700'}`}>
                    <strong>Note:</strong> Budget will be created as Draft status. You can review and edit it before submitting for approval.
                  </div>
                </>
              )}

              {importType !== 'budget' && (
                <div className={`p-3 rounded-sm text-sm ${isDark ? 'bg-amber-900/20 text-amber-400' : 'bg-amber-50 text-amber-700'}`}>
                  <strong>Note:</strong> {importType === 'actuals' ? 'Actuals' : 'Cashflow'} import functionality coming soon. File will be uploaded for reference.
                </div>
              )}
            </div>
          )}

          {!importProcessing && (
            <DialogFooter className="gap-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setImportDialog({ open: false, file: null })} 
                className="rounded-sm"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleImportSubmit}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import {importType === 'budget' ? 'as Budget' : 'File'}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImportExportPage;
